import { AddIcon, MinusIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FiSun } from "react-icons/fi";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { trackEvent } from "../../../../shared/analytics";
import {
  checkGoalFeasibility,
  createGoal,
  getGoalOrders,
} from "../../../../services/goalsApi";
import {
  GOAL_TYPES,
  RATE_PERIODS,
  RATE_QUICK_PICKS,
  DEFAULT_RATE_TARGET,
  DEFAULT_YEARLY_TARGET,
  YEARLY_QUICK_PICKS,
  WEEKDAYS,
  addDaysIso,
  addMonthsIso,
  feasibilityCopy,
  formatShortDate,
  rateInfoCopy,
  rateResetsLabel,
  todayIso,
} from "../../helpers/goals";

const TYPE_KEYS = ["deadline", "rate", "yearly"];
const QUICK_DATES = [
  { label: "+2 weeks", apply: () => addDaysIso(14) },
  { label: "+1 month", apply: () => addMonthsIso(1) },
  { label: "+3 months", apply: () => addMonthsIso(3) },
];
const DEFAULT_ORDER_RESULTS = 3;
const MAX_ORDER_RESULTS = 8;

const OrderRow = ({ order }) => (
  <>
    <Image
      alt=""
      bg="#101426"
      borderRadius="md"
      flexShrink="0"
      h="54px"
      objectFit="cover"
      src={order.thumbnail}
      w="38px"
    />
    <Box flex="1" minW="0" px="3" textAlign="left">
      <Text color="gray.100" fontSize="sm" fontWeight="600" noOfLines={2}>
        {order.title}
      </Text>
      <Text color="gray.400" fontSize="xs">
        {`${order.totalRead} of ${order.totalComics} read`}
      </Text>
    </Box>
  </>
);

/**
 * Pick one of the user's in-progress orders. The API lists them most
 * recently read first, so the three shown by default are the ones being read
 * right now; typing part of a title searches the whole library locally.
 */
const OrderAutocomplete = ({ orders, selected, onSelect }) => {
  const [query, setQuery] = useState("");
  const normalized = query.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      normalized
        ? orders.filter((order) =>
            order.title.toLowerCase().includes(normalized)
          )
        : orders,
    [orders, normalized]
  );
  const matches = filtered.slice(
    0,
    normalized ? MAX_ORDER_RESULTS : DEFAULT_ORDER_RESULTS
  );
  const hiddenCount = filtered.length - matches.length;

  if (selected) {
    return (
      <Flex
        align="center"
        bg="#1A2138"
        border="1px solid"
        borderColor="#30384E"
        borderRadius="lg"
        p="2.5"
      >
        <OrderRow order={selected} />
        <Button
          aria-label="Change reading order"
          borderColor="#30384E"
          borderRadius="full"
          color="gray.300"
          flexShrink="0"
          onClick={() => {
            setQuery("");
            onSelect("");
          }}
          size="xs"
          variant="outline"
        >
          Change
        </Button>
      </Flex>
    );
  }

  return (
    <Box>
      <InputGroup>
        <InputLeftElement color="gray.500" pointerEvents="none">
          <SearchIcon boxSize="3.5" />
        </InputLeftElement>
        <Input
          aria-label="Search your reading orders"
          autoComplete="off"
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search your reading orders"
          value={query}
        />
      </InputGroup>
      <Box mt="1">
        {matches.map((order) => (
          <Flex
            align="center"
            aria-label={order.title}
            as="button"
            borderBottom="1px solid"
            borderColor="#222B45"
            key={order.readingOrderId}
            onClick={() => onSelect(String(order.readingOrderId))}
            py="2.5"
            type="button"
            w="100%"
            _hover={{ bg: "#1A2138" }}
          >
            <OrderRow order={order} />
          </Flex>
        ))}
        {matches.length === 0 && (
          <Text color="gray.400" fontSize="xs" px="1" py="3">
            {`No reading order matches "${query.trim()}".`}
          </Text>
        )}
        {hiddenCount > 0 && (
          <Text color="gray.400" fontSize="xs" px="1" py="3">
            {normalized
              ? `${hiddenCount} more · keep typing to narrow it down.`
              : `Search to find your other ${hiddenCount} reading ${
                  hiddenCount === 1 ? "order" : "orders"
                }.`}
          </Text>
        )}
      </Box>
    </Box>
  );
};
const FEASIBILITY_DEBOUNCE_MS = 400;
const RATE_MAX = 999;
const PERIOD_LABELS = { day: "Day", week: "Week", month: "Month" };

const clampRate = (value) =>
  Math.min(Math.max(Math.round(Number(value) || 0), 1), RATE_MAX);
const YEARLY_MAX = 100000;
const clampYearly = (value) =>
  Math.min(Math.max(Math.round(Number(value) || 0), 1), YEARLY_MAX);

/**
 * Creation sheet: type, two or three fields, a live feasibility check and
 * the actions. The warning never blocks; the point is informed consent.
 */
const GoalCreateModal = ({ isOpen, onClose, initial = {} }) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [type, setType] = useState(initial.type || "deadline");
  const [readingOrderId, setReadingOrderId] = useState(
    initial.readingOrderId ? String(initial.readingOrderId) : ""
  );
  const [endsOn, setEndsOn] = useState(initial.endsOn || "");
  const [rateAmount, setRateAmount] = useState(
    String(DEFAULT_RATE_TARGET.week)
  );
  const [period, setPeriod] = useState("week");
  const [resetDay, setResetDay] = useState("1");
  const [yearlyTarget, setYearlyTarget] = useState(
    String(DEFAULT_YEARLY_TARGET)
  );
  const [feasibility, setFeasibility] = useState(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef();
  const year = new Date().getFullYear();

  const ordersQuery = useQuery(["goalOrders"], getGoalOrders, {
    enabled: isOpen && type === "deadline",
    staleTime: 60 * 1000,
  });
  const ordersData = ordersQuery.data?.data;
  const orders = useMemo(
    () => (Array.isArray(ordersData) ? ordersData : []),
    [ordersData]
  );
  const selectedOrder = orders.find(
    (order) => String(order.readingOrderId) === String(readingOrderId)
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setType(initial.type || "deadline");
    setReadingOrderId(
      initial.readingOrderId ? String(initial.readingOrderId) : ""
    );
    setEndsOn(initial.endsOn || "");
    setFeasibility(null);
    setError(null);
    // Only reset when the sheet (re)opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (
      !readingOrderId &&
      orders.length === 1 &&
      type === "deadline" &&
      !initial.readingOrderId
    ) {
      setReadingOrderId(String(orders[0].readingOrderId));
    }
  }, [orders, readingOrderId, type, initial.readingOrderId]);

  const payload = useMemo(() => {
    if (type === "deadline") {
      if (!readingOrderId || !endsOn) {
        return null;
      }
      // A deadline goal is always the whole order; "Lower the target" lives
      // in the manage sheet once the goal exists.
      return { type, readingOrderId: Number(readingOrderId), endsOn };
    }
    if (type === "rate") {
      if (!Number(rateAmount)) {
        return null;
      }
      return {
        type,
        targetCount: Number(rateAmount),
        period,
        ...(period === "week" ? { periodResetDay: Number(resetDay) } : {}),
      };
    }
    if (!Number(yearlyTarget)) {
      return null;
    }
    return { type, targetCount: Number(yearlyTarget), year };
  }, [
    type,
    readingOrderId,
    endsOn,
    rateAmount,
    period,
    resetDay,
    yearlyTarget,
    year,
  ]);

  useEffect(() => {
    if (!isOpen || !payload) {
      setFeasibility(null);
      return undefined;
    }
    setChecking(true);
    clearTimeout(debounceRef.current);
    let cancelled = false;
    debounceRef.current = setTimeout(() => {
      checkGoalFeasibility(payload)
        .then((response) => {
          if (!cancelled) {
            setFeasibility(response?.data || null);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setFeasibility(null);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setChecking(false);
          }
        });
    }, FEASIBILITY_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(debounceRef.current);
    };
  }, [isOpen, payload]);

  const createMutation = useMutation((goal) => createGoal(goal), {
    onSuccess: (_, variables) => {
      trackEvent("goal_created", {
        type: variables?.type,
        source: variables?.source,
        replaced_existing: Boolean(variables?.replace),
      });
      queryClient.invalidateQueries(["goals"]);
      queryClient.invalidateQueries(["goalOrders"]);
      toast({
        title: "Goal set.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      onClose();
    },
  });

  const submit = (replace = false) => {
    if (!payload) {
      return;
    }
    setError(null);
    createMutation
      .mutateAsync({
        ...payload,
        source: initial.source || "manual",
        sourceRefId: initial.sourceRefId || null,
        ...(replace ? { replace: true } : {}),
      })
      .catch((requestError) => {
        const status = requestError?.response?.status;
        if (status === 409 && !replace) {
          if (window.confirm("Replace your existing goal on this order?")) {
            submit(true);
          }
          return;
        }
        const data = requestError?.response?.data;
        const firstFieldError = data?.errors
          ? Object.values(data.errors).flat()[0]
          : null;
        setError(firstFieldError || data?.message || "Could not set the goal.");
      });
  };

  const copy = feasibilityCopy(feasibility, type);
  const isWarning = feasibility?.tone === "warning";
  const rateCount = clampRate(rateAmount);
  const yearlyCount = clampYearly(yearlyTarget);
  const rateInfo = rateInfoCopy(
    rateCount,
    period,
    feasibility?.minutesPerIssue,
    feasibility?.periodDays
  );
  const showRateTip =
    type === "rate" &&
    !!feasibility?.hasHistory &&
    Number(feasibility?.recentPerPeriod) >= 1;

  const lowerTarget = () => {
    if (!feasibility) {
      return;
    }
    const average = Math.max(Number(feasibility.recentDailyAverage) || 0, 0);
    if (type === "rate") {
      const perPeriod =
        feasibility.recentPerPeriod ??
        average * (Number(feasibility.periodDays) || 1);
      setRateAmount(String(Math.max(Math.floor(perPeriod), 1)));
    } else if (type === "yearly") {
      const remainingDays = Number(feasibility.daysRemaining) || 0;
      setYearlyTarget(
        String(
          clampYearly(
            Math.floor(average * remainingDays) +
              (Number(feasibility.completed) || 0)
          )
        )
      );
    }
  };

  // Keep the rate default in step with the period unless the user typed one.
  const changePeriod = (next) => {
    setPeriod(next);
    const current = Number(rateAmount);
    if (
      Object.values(DEFAULT_RATE_TARGET).includes(current) ||
      RATE_QUICK_PICKS[period].includes(current)
    ) {
      setRateAmount(String(DEFAULT_RATE_TARGET[next]));
    }
  };

  const laterDateLabel = (iso) => {
    const yearOf = Number(String(iso || "").slice(0, 4));
    return `Pick a later date · ${formatShortDate(iso)}${
      yearOf && yearOf !== year ? ` ${yearOf}` : ""
    }`;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg="#171C2D" border="1px solid" borderColor="#30384E">
        <ModalHeader fontSize="2xl">New goal</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <RadioGroup onChange={setType} value={type}>
            <Stack
              border="1px solid"
              borderColor="#30384E"
              borderRadius="lg"
              overflow="hidden"
              spacing="0"
            >
              {TYPE_KEYS.map((key, index) => (
                <Box
                  bg={type === key ? "#2A3350" : "#1A2138"}
                  borderTop={index ? "1px solid" : undefined}
                  borderColor="#30384E"
                  key={key}
                  px="18px"
                  py="4"
                >
                  <Radio colorScheme="red" value={key}>
                    <Text fontWeight="semibold">{GOAL_TYPES[key].label}</Text>
                    <Text color="gray.400" fontSize="sm">
                      {GOAL_TYPES[key].description}
                    </Text>
                  </Radio>
                </Box>
              ))}
            </Stack>
          </RadioGroup>

          {type === "deadline" && (
            <>
              <FormControl isRequired mt="5">
                <FormLabel color="gray.400" fontSize="sm">
                  Reading order
                </FormLabel>
                {ordersQuery.isLoading ? (
                  <Spinner size="sm" />
                ) : orders.length ? (
                  <OrderAutocomplete
                    onSelect={setReadingOrderId}
                    orders={orders}
                    selected={selectedOrder}
                  />
                ) : (
                  <Text color="gray.400" fontSize="sm">
                    Start a reading order first, then set a goal on it.
                  </Text>
                )}
              </FormControl>
              <FormControl isRequired mt="4">
                <FormLabel color="gray.400" fontSize="sm">
                  Completed by
                </FormLabel>
                <Input
                  aria-label="Completed by"
                  min={todayIso()}
                  onChange={(event) => setEndsOn(event.target.value)}
                  type="date"
                  value={endsOn}
                />
                <Flex gap="2" mt="2" wrap="wrap">
                  {QUICK_DATES.map((quick) => (
                    <Button
                      borderColor="#30384E"
                      borderRadius="full"
                      color="gray.300"
                      key={quick.label}
                      onClick={() => setEndsOn(quick.apply())}
                      size="xs"
                      variant="outline"
                    >
                      {quick.label}
                    </Button>
                  ))}
                </Flex>
              </FormControl>
              <Text color="gray.500" fontSize="xs" mt="3">
                The goal is the whole order. Skipped issues count towards
                finishing it; they do not count for rate or yearly goals.
              </Text>
            </>
          )}

          {type === "rate" && (
            <Box mt="5">
              <Text color="gray.400" fontSize="sm" mb="2">
                Per
              </Text>
              <Flex
                bg="#1A2138"
                border="1px solid"
                borderColor="#30384E"
                borderRadius="lg"
                p="1"
                role="radiogroup"
                aria-label="Per"
              >
                {RATE_PERIODS.map((option) => (
                  <Button
                    aria-checked={period === option}
                    bg={period === option ? "#0F1322" : "transparent"}
                    color={period === option ? "white" : "gray.400"}
                    _hover={{ bg: period === option ? "#0F1322" : "#222B45" }}
                    flex="1"
                    fontWeight={period === option ? "700" : "500"}
                    key={option}
                    onClick={() => changePeriod(option)}
                    role="radio"
                    size="md"
                    variant="ghost"
                  >
                    {PERIOD_LABELS[option]}
                  </Button>
                ))}
              </Flex>

              <Text color="gray.400" fontSize="sm" mt="5" mb="2">
                How many comics
              </Text>
              <Flex align="center" justify="space-between">
                <IconButton
                  aria-label="Fewer comics"
                  borderColor="#30384E"
                  borderRadius="full"
                  h="48px"
                  icon={<MinusIcon boxSize="3" />}
                  isDisabled={rateCount <= 1}
                  minW="48px"
                  onClick={() =>
                    setRateAmount(String(clampRate(rateCount - 1)))
                  }
                  variant="outline"
                  w="48px"
                />
                <Box textAlign="center">
                  <Text fontSize="4xl" fontWeight="700" lineHeight="1">
                    {rateCount}
                  </Text>
                  <Text color="gray.400" fontSize="sm" mt="1">
                    a {period}
                  </Text>
                </Box>
                <IconButton
                  aria-label="More comics"
                  borderColor="#30384E"
                  borderRadius="full"
                  h="48px"
                  icon={<AddIcon boxSize="3" />}
                  isDisabled={rateCount >= RATE_MAX}
                  minW="48px"
                  onClick={() =>
                    setRateAmount(String(clampRate(rateCount + 1)))
                  }
                  variant="outline"
                  w="48px"
                />
              </Flex>
              <SimpleGrid columns={4} mt="4" spacing="2">
                {RATE_QUICK_PICKS[period].map((pick) => (
                  <Button
                    aria-label={`${pick} a ${period}`}
                    aria-pressed={rateCount === pick}
                    bg={rateCount === pick ? "#2A3148" : "transparent"}
                    borderColor={rateCount === pick ? "red.500" : "#30384E"}
                    borderRadius="full"
                    color="gray.100"
                    _hover={{ bg: "#222B45" }}
                    key={pick}
                    onClick={() => setRateAmount(String(pick))}
                    size="sm"
                    variant="outline"
                  >
                    {pick}
                  </Button>
                ))}
              </SimpleGrid>

              {/* Informational, never a warning: the tip row below is the
                  honest comparison, and a shouting box teaches avoidance. */}
              <Box
                bg="#1A2138"
                border="1px solid"
                borderColor="#30384E"
                borderRadius="lg"
                mt="4"
                px="4"
                py="3"
              >
                <Text color="gray.100" fontWeight="bold">
                  {rateInfo.headline}
                </Text>
                <Text color="gray.300" fontSize="sm">
                  {rateInfo.detail}
                </Text>
                {!!feasibility?.premiumNote && (
                  <Text color="#FFC94D" fontSize="xs" mt="2">
                    {feasibility.premiumNote}
                  </Text>
                )}
              </Box>

              {showRateTip && (
                <Flex
                  align="center"
                  border="1px dashed"
                  borderColor="#30384E"
                  borderRadius="lg"
                  gap="3"
                  mt="3"
                  px="4"
                  py="3"
                >
                  <Box as={FiSun} color="gray.400" flexShrink="0" />
                  <Text color="gray.300" flex="1" fontSize="sm">
                    You've averaged{" "}
                    <Text as="span" color="white" fontWeight="600">
                      {feasibility.recentPerPeriod} a {period}
                    </Text>{" "}
                    recently
                  </Text>
                  <Button
                    color="#42AAFF"
                    onClick={() =>
                      setRateAmount(
                        String(clampRate(feasibility.recentPerPeriod))
                      )
                    }
                    size="sm"
                    variant="link"
                  >
                    Use {feasibility.recentPerPeriod}
                  </Button>
                </Flex>
              )}

              <Flex
                align="center"
                borderTop="1px solid"
                borderColor="#30384E"
                justify="space-between"
                mt="4"
                pt="4"
              >
                <Text color="gray.300" fontWeight="600">
                  Resets
                </Text>
                {period === "week" ? (
                  <Select
                    aria-label="Resets on"
                    maxW="180px"
                    onChange={(event) => setResetDay(event.target.value)}
                    size="sm"
                    value={resetDay}
                    variant="unstyled"
                    textAlign="right"
                    color="gray.200"
                  >
                    {WEEKDAYS.map((day, index) => (
                      <option key={day} value={index}>
                        Every {day}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Text color="gray.400">{rateResetsLabel(period)}</Text>
                )}
              </Flex>
              <Text color="gray.500" fontSize="xs" mt="3">
                Only comics marked read count. A missed period simply ends and
                the next begins.
              </Text>
            </Box>
          )}

          {type === "yearly" && (
            <Box mt="5">
              <Text color="gray.400" fontSize="sm" mb="2">
                Target for the year
              </Text>
              <Flex align="center" justify="space-between">
                <IconButton
                  aria-label="Fewer comics"
                  borderColor="#30384E"
                  borderRadius="full"
                  h="48px"
                  icon={<MinusIcon boxSize="3" />}
                  isDisabled={yearlyCount <= 1}
                  minW="48px"
                  onClick={() =>
                    setYearlyTarget(String(clampYearly(yearlyCount - 1)))
                  }
                  variant="outline"
                  w="48px"
                />
                <Box textAlign="center">
                  <Text fontSize="4xl" fontWeight="700" lineHeight="1">
                    {yearlyCount}
                  </Text>
                  <Text color="gray.400" fontSize="sm" mt="1">
                    comics in {year}
                  </Text>
                </Box>
                <IconButton
                  aria-label="More comics"
                  borderColor="#30384E"
                  borderRadius="full"
                  h="48px"
                  icon={<AddIcon boxSize="3" />}
                  isDisabled={yearlyCount >= YEARLY_MAX}
                  minW="48px"
                  onClick={() =>
                    setYearlyTarget(String(clampYearly(yearlyCount + 1)))
                  }
                  variant="outline"
                  w="48px"
                />
              </Flex>
              <SimpleGrid columns={4} mt="4" spacing="2">
                {YEARLY_QUICK_PICKS.map((pick) => (
                  <Button
                    aria-label={`${pick.value} comics, ${pick.caption}`}
                    aria-pressed={yearlyCount === pick.value}
                    bg={yearlyCount === pick.value ? "#2A3148" : "transparent"}
                    borderColor={
                      yearlyCount === pick.value ? "red.500" : "#30384E"
                    }
                    borderRadius="lg"
                    color="gray.100"
                    flexDirection="column"
                    h="auto"
                    _hover={{ bg: "#222B45" }}
                    key={pick.value}
                    onClick={() => setYearlyTarget(String(pick.value))}
                    py="3"
                    variant="outline"
                  >
                    <Text fontSize="lg" fontWeight="700" lineHeight="1">
                      {pick.value}
                    </Text>
                    <Text
                      color="gray.400"
                      fontSize="xs"
                      fontWeight="400"
                      mt="1"
                    >
                      {pick.caption}
                    </Text>
                  </Button>
                ))}
              </SimpleGrid>
              <Text color="gray.400" fontSize="sm" mt="3">
                Runs 1 Jan to 31 Dec {year}. Reading already done this year
                counts.
              </Text>
            </Box>
          )}

          {type !== "rate" && (copy || checking) && (
            // Informational, never a warning: the numbers and the offered
            // correction are the message. Amber teaches avoidance.
            <Flex
              align="flex-start"
              bg="#1A2138"
              border="1px solid"
              borderColor="#30384E"
              borderRadius="lg"
              gap="3"
              mt="5"
              px="4"
              py="3"
              role="status"
            >
              <Box flex="1">
                {copy ? (
                  <>
                    <Text color="gray.100" fontWeight="bold">
                      {copy.headline}
                    </Text>
                    <Text color="gray.300" fontSize="sm">
                      {copy.detail}
                    </Text>
                    {isWarning &&
                      type === "deadline" &&
                      !!feasibility?.suggestedEndDate && (
                        <Button
                          colorScheme="blue"
                          mt="2"
                          onClick={() =>
                            setEndsOn(feasibility.suggestedEndDate)
                          }
                          size="sm"
                          variant="link"
                        >
                          {laterDateLabel(feasibility.suggestedEndDate)}
                        </Button>
                      )}
                    {isWarning && type === "yearly" && (
                      <Button
                        colorScheme="blue"
                        mt="2"
                        onClick={lowerTarget}
                        size="sm"
                        variant="link"
                      >
                        Lower the target
                      </Button>
                    )}
                  </>
                ) : (
                  <Spinner size="xs" />
                )}
              </Box>
            </Flex>
          )}

          {!!error && (
            <Text color="red.300" fontSize="sm" mt="3">
              {error}
            </Text>
          )}
        </ModalBody>
        <ModalFooter gap="3">
          <Button borderColor="#30384E" onClick={onClose} variant="outline">
            Back
          </Button>
          <Button
            bg="white"
            color="gray.900"
            _hover={{ bg: "gray.200" }}
            flex="1"
            isDisabled={!payload}
            isLoading={createMutation.isLoading}
            onClick={() => submit(false)}
          >
            Set goal
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default GoalCreateModal;
